import React from 'react';
import { Field, Form } from 'react-final-form';
import { useLocation } from 'react-router';
import PropTypes from 'prop-types';
import cx from 'classnames';

import { MDText } from 'i18n-react';

import { Input } from '@motorway/motorway-storybook-cra';
import { Button } from '@motorway/mw-highway-code';
import { BUTTON_TYPE, SIZE, VARIANT } from '@motorway/mw-highway-code/enums';

import cypressIds from 'CypressId';

import { useFeatureFlagActive } from 'Utilities/analytics/kameleoon/hooks/useFeatureFlagActive';
import { vehicleType } from 'Utilities/helpers';
import { applyCypressData } from 'Utilities/index';

import useHeroVrmInput from '../hooks/useHeroVrmInput';

import LocalTexts from './VrmInputTexts.json';

import styles from './VrmInputVRM.scss';

const LocalT = new MDText(LocalTexts);

// TODO - Move these over to RFF such as '../mileageInput/MileageInput.jsx'
const VrmInputVRM = ({
  analyticsEvents,
  loadingHandler,
  onError,
  onSuccess,
  setShowMileage,
  valuationHub,
  vrm,
}) => {
  const location = useLocation();

  const {
    getErrorMsg,
    hasApiError: isApiError,
    loading,
    onFormSubmit,
    onInputBlur,
    onInputChange,
    onInputFocus,
    showIntent,
    vrmValue,
    wrongVRM,
  } = useHeroVrmInput({
    loadingHandler,
    onError,
    onSuccess,
    setShowMileage,
    valuationHub,
    vrm,
  });

  const placeholder = LocalT.translate('vrmChecker.placeholder');
  const ctaNonErrorLabel = LocalT.translate(valuationHub
    ? 'cta.startTracking'
    : 'cta.valueCar', { vehicle: vehicleType(location.pathname) });

  const ctaLabel = (isApiError || wrongVRM)
    ? LocalT.translate('cta.again')
    : ctaNonErrorLabel;

  const commonInputAttributes = {
    ...applyCypressData(cypressIds.fields.vrmInputField),
    onBlur: onInputBlur,
    onChange: ({ currentTarget: { value } }) => onInputChange(value),
    onFocus: () => {
      analyticsEvents?.onInputFocus?.();
      analyticsEvents?.onLandingPageInputFocus?.();
      onInputFocus();
    },
    placeholder,
  };

  const isGreenBorderVariation = useFeatureFlagActive(
    (flags) => flags.tc_vrm_multivariant,
    { active: ((value) => value === 'green_border') },
  );

  const isLargeVRMFontVariation = useFeatureFlagActive(
    (flags) => flags.tc_vrm_multivariant,
    { active: ((value) => value === 'larger_vrm_font') },
  );

  const isSmallVRMFontVariation = useFeatureFlagActive(
    (flags) => flags.tc_vrm_multivariant,
    { active: ((value) => value === 'smaller_vrm_font') },
  );

  return (
    <>
      <Form
        initialValues={{ 'vrm-input': vrmValue }}
        onSubmit={() => onFormSubmit()}
        render={({ handleSubmit }) => (
          <form
            className={styles.component}
            data-cy="vrmInputForm"
            onSubmit={handleSubmit}
          >
            <Field
              id='vrm-input'
              name='vrm-input'
            >
              { ({ id }) => (
                <Input
                  large
                  noIcon
                  vrm
                  className={ cx(styles.input, {
                    [styles.error]: isApiError,
                    [styles.greenBorderVariation]: isGreenBorderVariation,
                    [styles.largeVRMFontVariation]: isLargeVRMFontVariation,
                    [styles.smallVRMFontVariation]: isSmallVRMFontVariation,
                  }) }
                  data-cy="invalidVrm"
                  feedbackTooltip={(!wrongVRM && {
                    content: LocalT.translate(`vrmChecker.${getErrorMsg()}`),
                    placement: 'top',
                  }) || undefined}
                  inputProps={{
                    id,
                    input: {
                      ...commonInputAttributes,
                      'aria-label': 'vrm-input',
                      autoCapitalize: 'characters',
                      autoComplete: 'off',
                      maxLength: 10,
                      name: id,
                      readOnly: loading,
                      type: 'text',
                      value: vrmValue,
                    },
                  }}
                  intent={showIntent()}
                />)}
            </Field>
            <Button
              data-thc-button
              fullWidth
              icon="chevron"
              label= {ctaLabel}
              loading={loading}
              onClick={() => analyticsEvents?.onButtonClick?.()}
              size={ SIZE.LARGE }
              type={BUTTON_TYPE.SUBMIT}
              variant={VARIANT.PRIMARY}
              {...applyCypressData(cypressIds.buttons.valueCarButton)}
            />
          </form>
        )}
      />
    </>
  );
};

VrmInputVRM.defaultProps = {
  analyticsEvents: {},
  isHomePageRedesign: false,
  loadingHandler: () => { },
  onError: () => { },
  onSuccess: () => { },
  setShowMileage: () => { },
  valuationHub: false,
  vrm: '',
};

VrmInputVRM.propTypes = {
  analyticsEvents: PropTypes.shape(),
  isHomePageRedesign: PropTypes.bool,
  loadingHandler: PropTypes.func,
  onError: PropTypes.func,
  onSuccess: PropTypes.func,
  setShowMileage: PropTypes.func,
  valuationHub: PropTypes.bool,
  vrm: PropTypes.string,
};

export default VrmInputVRM;
